import React from 'react'
import ContactInformationForm from './ContactInformationForm'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import effects from 'app/effects/effects'
import { useDispatch } from 'react-redux'
import MessageAlert from 'app/components/MessageAlert'
import { useIntl } from 'react-intl'
import { isNil, includes, prop } from 'ramda'
import {
  validatePhoneNumber,
  isOnlyCountryCallingCode,
} from 'app/componentV4/Input/PhoneNumberInputV4'
import { contactMappingToApi } from '../mapping/profileMapping'
import {
  CONTACT_TYPE_APP,
  CONTACT_TYPE_REGISTERED,
} from 'app/main/notification/socialMediaSelect/socialMediaSelectConfig'
import { useFormContext, TEXT_255_MAX } from '../FormProvider'

const hasCRMReceiveMethods = (subscriber) => {
  return subscriber.receiveMethods.some((item) =>
    includes(item.contactType, [CONTACT_TYPE_APP, CONTACT_TYPE_REGISTERED]),
  )
}

const validationSchema = (subscriber, formatMessage) => {
  return Yup.object({
    preferredLanguage: Yup.number().required(
      formatMessage({ id: 'composeDialog.field.required' }),
    ),
    preferredChannel: Yup.object().required(
      formatMessage({ id: 'preferred.language.error.text' }),
    ),

    email: Yup.string()
      .matches(
        /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
        formatMessage({ id: 'login.textField.validation.email' }),
      )
      .test(
        'email-required',
        formatMessage({ id: 'composeDialog.field.required' }),
        (value, context) => {
          if (hasCRMReceiveMethods(subscriber)) return true

          const { phoneNumber, automatedCallPhoneNumber } = context.parent
          return value || phoneNumber || automatedCallPhoneNumber
        },
      )
      .max(
        TEXT_255_MAX,
        formatMessage(
          { id: 'maximum.characters.allowed' },
          { limit: TEXT_255_MAX },
        ),
      ),

    phoneNumber: Yup.string()
      .test(
        'is-valid-phone',
        formatMessage({ id: 'crm.form.validation.url' }),
        (value) => {
          const notHavePhoneNumber = isOnlyCountryCallingCode(value)
          if (isNil(value) || notHavePhoneNumber) return true
          return validatePhoneNumber(value)
        },
      )
      .test(
        'phoneNumber-required',
        formatMessage({ id: 'composeDialog.field.required' }),
        (value, context) => {
          if (hasCRMReceiveMethods(subscriber)) return true

          const { email, automatedCallPhoneNumber } = context.parent
          return value || email || automatedCallPhoneNumber
        },
      ),

    automatedCallPhoneNumber: Yup.string()
      .test(
        'is-valid-automatedCallPhoneNumber format',
        formatMessage({ id: 'crm.form.validation.url' }),
        (value) => {
          if (isNil(value)) return true
          return validatePhoneNumber(value)
        },
      )
      .test(
        'automatedCallPhoneNumber-required',
        formatMessage({ id: 'composeDialog.field.required' }),
        (value, context) => {
          if (hasCRMReceiveMethods(subscriber)) return true

          const { email, phoneNumber } = context.parent
          return value || email || phoneNumber
        },
      ),

    contactable: Yup.mixed().oneOf([0, 1], 'Contactable must be 0 or 1'),
  })
}

export default ({ initailValue, subscriber, languages, refetchData }) => {
  const subscriberId = prop('id', subscriber)
  const dispatch = useDispatch()
  const { onContactChange, onContactReset } = useFormContext()

  const { formatMessage } = useIntl()
  const updateGeneralInfo = (
    values,
    { resetForm, setSubmitting, setFieldError },
  ) => {
    const info = { type: 'contact', payload: contactMappingToApi(values) }
    effects.apis
      .updateProfileDetailOfSubscriber({ subscriberId, info })
      .then((response) => {
        setSubmitting(false)
        onContactReset()
        setTimeout(() => {
          refetchData(subscriberId)
          resetForm({ values })
          dispatch(
            MessageAlert.showSuccess({
              message: formatMessage({ id: 'message.alert.success' }),
            }),
          )
        }, 500)
      })
      .catch((error) => {
        setSubmitting(false)
        const errorCode = error?.response?.data?.details?.code
        if (errorCode === 403) {
          dispatch(
            MessageAlert.showError({
              message: formatMessage({ id: 'error.permission.denied' }),
            }),
          )
        } else if (errorCode === 618) {
          setFieldError(
            'uniqueId',
            formatMessage({ id: 'uniqueId.already.exist' }),
          )
        } else {
          dispatch(
            MessageAlert.showError({
              message: formatMessage({ id: 'message.alert.error' }),
            }),
          )
        }
      })
  }

  return (
    <div className="w-full">
      <Formik
        initialValues={initailValue}
        validationSchema={validationSchema(subscriber, formatMessage)}
        onSubmit={updateGeneralInfo}
        enableReinitialize={true}
      >
        <Form>
          <ContactInformationForm
            subscriber={subscriber}
            languages={languages}
            onContactChange={onContactChange}
            onContactReset={onContactReset}
          />
        </Form>
      </Formik>
    </div>
  )
}
